/* eslint-disable max-lines */
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';
import { Params, useParams } from 'react-router-dom';
import * as yup from 'yup';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

import Button from '@components/atoms/ui-kit/button.tsx';
import PageHeader from '@components/layout/page-header';
import Loader from '@components/atoms/loaders/loader';
import ConfirmationModal from '@components/atoms/modals/confirmation-modal.tsx';
import DataGridV2, {
  type DataGridColumn
} from '@components/atoms/datatable/data-grid-v2';
import { GridAction } from '@components/atoms/datatable/grid-action-controller';
import CreateNewWebhookForm from '@components/back-office/customer/create-new-webhook-form';
import UpdateWebhookForm from '@components/back-office/customer/update-webhook-form';

import {
  useGetWebhooks,
  WebhookCustomers
} from '@utils/api/back-office/webhooks/use-get-webhooks';
import { useDeleteWebhook } from '@utils/api/back-office/webhooks/use-delete-webhook';
import { CreateWebhookPayload } from '@utils/api/back-office/webhooks/use-create-webhook';
import { Badge } from '@components/atoms/ui-kit/badge';
import { PencilLine, Plus, Trash2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { AxiosError } from 'axios';
import { BaseError } from '@utils/types/shared/base';

type Row = {
  id: number;
  type: string;
  webhook_url: string;
  webhook_method: string;
  webhook_headers: string;
};

const WebhookTable = () => {
  const { t } = useTranslation();
  const { customerId } = useParams<Params>();
  const cid = Number(customerId) || 0;
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [selectedWebhook, setSelectedWebhook] = useState<number>();
  const [selectedRow, setSelectedRow] = useState<Row | undefined>();
  const [deleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [editModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const { data, isLoading } = useGetWebhooks(cid);
  const { mutate: deleteWebhook } = useDeleteWebhook();

  const schema = yup.object().shape({
    type: yup
      .string()
      .oneOf(
        [
          'REVIEWER_CALLBACK',
          'ADMIN_CALLBACK',
          'ADMIN_FROM_SESSION_CALLBACK',
          'EXCEEDANCE_NOTIFICATION'
        ],
        t('invalid-type')
      )
      .required(t('type-is-required')),
    webhook_url: yup
      .string()
      .url(t('webhook-url-must-be-valid'))
      .required(t('webhook-url-is-required')),
    webhook_method: yup
      .string()
      .oneOf(['POST', 'GET', 'PUT'], t('invalid-method'))
      .required(t('webhook-method-is-required')),
    webhook_headers: yup
      .array()
      .of(yup.string())
      .required(t('webhook-headers-are-required'))
  });

  const {
    register,
    handleSubmit,
    control,
    setValue,
    getValues,
    reset,
    watch,
    formState: { errors }
  } = useForm<CreateWebhookPayload>({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resolver: yupResolver(schema) as any,
    defaultValues: {
      type: '',
      webhook_url: '',
      webhook_method: '',
      webhook_headers: []
    }
  });

  const rows = useMemo<Row[]>(() => {
    if (!data?.result) return [];
    return data.result.map((w: WebhookCustomers) => ({
      id: w.id,
      type: w.type,
      webhook_url: w.webhook_url,
      webhook_method: w.webhook_method,
      webhook_headers: (w.webhook_headers ?? []).join(', ')
    }));
  }, [data]);

  const getMethodColor = (
    method: string
  ): 'blue' | 'amber' | 'emerald' | 'zinc' => {
    switch (method) {
      case 'POST':
        return 'blue';
      case 'PUT':
        return 'amber';
      case 'GET':
        return 'emerald';
      default:
        return 'zinc';
    }
  };

  const columns = useMemo<DataGridColumn<Row>[]>(
    () => [
      {
        header: t('endpoint'),
        field: 'webhook_url',
        minWidth: 280,
        enableSorting: false,
        enableFiltering: false,
        filterVariant: 'text',
        enableRowCopyOnClick: true,
        cell: ({ getValue }) => {
          const url = String(getValue() ?? '');
          return (
            <div className="truncate max-w-md" title={url}>
              {url}
            </div>
          );
        }
      },
      {
        header: t('type'),
        field: 'type',
        minWidth: 240,
        enableSorting: false,
        enableFiltering: false,
        filterVariant: 'select',
        filterOptions: [
          { label: t('all'), value: '' },
          { label: 'REVIEWER_CALLBACK', value: 'REVIEWER_CALLBACK' },
          { label: 'ADMIN_CALLBACK', value: 'ADMIN_CALLBACK' },
          {
            label: 'ADMIN_FROM_SESSION_CALLBACK',
            value: 'ADMIN_FROM_SESSION_CALLBACK'
          },
          {
            label: 'EXCEEDANCE_NOTIFICATION',
            value: 'EXCEEDANCE_NOTIFICATION'
          }
        ],
        cell: ({ row }) => {
          const displayType = row.original.type.replace(/_/g, ' ');
          return <Badge color="zinc">{displayType}</Badge>;
        }
      },
      {
        header: t('method'),
        field: 'webhook_method',
        maxWidth: 120,
        enableSorting: false,
        enableFiltering: false,
        filterVariant: 'select',
        filterOptions: [
          { label: t('all'), value: '' },
          { label: 'POST', value: 'POST' },
          { label: 'GET', value: 'GET' },
          { label: 'PUT', value: 'PUT' }
        ],
        cell: ({ row }) => {
          const method = row.original.webhook_method;
          return <Badge color={getMethodColor(method)}>{method}</Badge>;
        }
      },
      {
        header: t('headers'),
        field: 'webhook_headers',
        minWidth: 180,
        enableSorting: false,
        enableFiltering: false,
        cell: ({ row }) => {
          const headers = row.original.webhook_headers;
          if (!headers) return '—';
          const headerArray = headers.split(', ').filter(Boolean);
          const count = headerArray.length;
          if (count === 0) return '—';
          return (
            <div className="flex items-center gap-1">
              <Badge color="zinc" title={headers}>
                {count} {count === 1 ? 'header' : 'headers'}
              </Badge>
            </div>
          );
        }
      }
    ],
    [t]
  );

  const handleCellClick = (row: Row) => {
    setSelectedWebhook(row.id);
    setSelectedRow(row);
  };

  const actions = useMemo<GridAction<Row>[]>(
    () => [
      {
        id: 'edit',
        label: t('edit'),
        icon: <PencilLine className="h-4 w-4" />,
        intent: 'primary',
        run: row => {
          setSelectedWebhook(row.id);
          setSelectedRow(row);
          setIsEditModalOpen(true);
        }
      },
      {
        id: 'delete',
        label: t('delete'),
        icon: <Trash2 className="h-4 w-4" />,
        intent: 'danger',
        run: row => {
          setSelectedWebhook(row.id);
          setSelectedRow(row);
          setIsDeleteModalOpen(true);
        }
      }
    ],
    [t]
  );

  const menuOptions = useMemo(
    () => [
      {
        id: 'edit',
        label: t('edit'),
        icon: <PencilLine className="h-4 w-4" />
      },
      {
        id: 'delete',
        label: t('delete'),
        icon: <Trash2 className="h-4 w-4" />
      }
    ],
    [t]
  );

  const handleSetMenuOptions = (item: Row) => {
    setSelectedWebhook(item.id);
    setSelectedRow(item);
  };

  const handleMenuOptionClick = (optionId: string) => {
    if (!selectedRow) return;
    if (optionId === 'edit') setIsEditModalOpen(true);
    else if (optionId === 'delete') setIsDeleteModalOpen(true);
  };

  const handleDeleteWebhook = async () => {
    if (!selectedWebhook || !cid) return;
    setIsDeleting(true);
    await new Promise(r => setTimeout(r, 300));
    deleteWebhook(
      { customerId: cid, webhookId: Number(selectedWebhook) },
      {
        onSuccess: () => {
          toast.success(t('webhook-deleted-successfully'));
          setIsDeleteModalOpen(false);
        },
        onError: (error: AxiosError<BaseError>) => {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const d = error.response?.data as any;
          const msg =
            d?.error_description ||
            d?.detail ||
            d?.error ||
            d?.message ||
            t('an-error-has-occurred');
          toast.error(String(msg));
        },
        onSettled: () => setIsDeleting(false)
      }
    );
  };

  return (
    <div>
      {isLoading ? (
        <div className="h-screen w-full flex justify-center items-center -mt-24">
          <Loader height={10} width={3} />
        </div>
      ) : (
        <div className={cn(' min-w-full space-y-4')}>
          <PageHeader
            title={t('Webhooks')}
            contentClassName={'py-0 pb-4'}
            caption={t(
              'list-of-all-the-webhooks-including-their-endpoints-and-actions'
            )}
          >
            <Button
              icon={<Plus width={14} />}
              variant="primary"
              label={t('create')}
              onClick={() => {
                reset({
                  type: '',
                  webhook_url: '',
                  webhook_method: '',
                  webhook_headers: []
                });
                setCreateModalOpen(true);
              }}
              size="small"
            />
          </PageHeader>

          <DataGridV2<Row>
            data={rows}
            columns={columns}
            actions={actions}
            enableRowSelection={false}
            enablePagination
            manualPagination
            enableSorting
            manualSorting
            enableFiltering
            manualFiltering
            totalItems={
              data?.total_result ?? data?.total_results ?? rows.length
            }
            pageSize={25}
            pageSizeOptions={[25, 50, 75, 100]}
            showColumnFilters={false}
            filterMode="popover"
            height="75vh"
            dense
            grid={false}
            striped
            loading={isLoading || !data}
            getRowId={row => String(row.id)}
            onCellClick={handleCellClick}
            menuOptions={menuOptions}
            setMenuOptions={handleSetMenuOptions}
            onMenuOptionClick={handleMenuOptionClick}
          />
        </div>
      )}

      {/* Create modal */}
      {createModalOpen && (
        <CreateNewWebhookForm
          createModalOpen={createModalOpen}
          setCreateModalOpen={setCreateModalOpen}
          handleSubmit={handleSubmit}
          getValues={getValues}
          reset={reset}
          errors={errors}
          control={control}
          register={register}
        />
      )}

      {/* Delete confirm */}
      <ConfirmationModal
        isOpen={deleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleDeleteWebhook}
        size="small"
        title={t('delete-webhook')}
        caption={t('are-you-sure-you-want-to-delete-this-webhook')}
        confirmLabel={isDeleting ? t('deleting') : t('delete')}
        cancelLabel={t('cancel')}
        confirmDisabled={isDeleting}
        destructive
        autoFocusConfirm
      />

      {/* Edit modal */}
      {selectedWebhook && editModalOpen && (
        <UpdateWebhookForm
          customerId={customerId}
          selectedWebhook={selectedWebhook}
          setValue={setValue}
          editModalOpen={editModalOpen}
          setIsEditModalOpen={setIsEditModalOpen}
          handleSubmit={handleSubmit}
          register={register}
          errors={errors}
          control={control}
          reset={reset}
          watch={watch}
        />
      )}
    </div>
  );
};

export default WebhookTable;
